import React, { useState, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import './ExpertView.scss';
import FiltersSidebar from '../../Components/FiltersSidebar/FiltersSidebar';
import TopBar from '../../Components/TopBar/TopBar';
import ExpertCard from '../../Components/ExpertCard/ExpertCard';
import PublishEditModal from '../../Components/PublishEditModal/PublishEditModal';
import useEffectOnce from '../../useEffectOnce';
import { serverAddr } from '../../networkConfig';

const PAGE_SIZE = 4;

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return timestamp;
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    }) + ' IST';
  } catch {
    return timestamp;
  }
};

export default function ExpertView() {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(['Live', 'Yet To Live', 'Updated']);
  const [publishOpen, setPublishOpen] = useState(false);
  const [cardsData, setCardsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  const activeFiltersCount = selectedFilters.length;

  useEffectOnce(() => {
    fetchExpertViews(1);
  });

  const fetchExpertViews = useCallback(async (page) => {
    try {
      setLoading(true);
      const response = await serverAddr.post('/publisher/expert_views/', {
        page_no: page,
        page_per_entries: PAGE_SIZE,
      });

      const rawData = response?.data;
      const data = Array.isArray(rawData) ? rawData : (rawData?.data || []);
      const paginationInfo = response?.pagination || rawData?.pagination || null;

      const formatted = data.map((item) => {
        const isLong = item.direction === 'LONG';
        return {
          id: item.id,
          coin: item.scrip_title || '',
          symbol: (item.scrip_title || '').charAt(0).toUpperCase(),
          price: item.price ?? 0,
          change: item.price_change ?? 0,
          percent: item.perc_diff ?? 0,
          date: formatTimestamp(item.published_on),
          risk: item.stop_loss_1 ?? 0,
          supportLow: item.entry_min ?? 0,
          supportHigh: item.entry_max ?? 0,
          resistanceLow: isLong ? item.target_1 ?? 0 : item.target_2 ?? 0,
          resistanceHigh: isLong ? item.target_2 ?? 0 : item.target_1 ?? 0,
          gap: item.remaining_target ?? 0,
          cmp: item.price ?? 0,
          thumbsDown: 0,
          thumbsUp: 0,
          direction: item.direction,
          leverage: item.leverage,
          confidenceScore: item.confidence_score,
          expertName: item.expert_name,
          tags: item.tags,
          note: item.note,
          chart: item.chart,
          targetPeriod: item.target_period,
          contractType: item.contract_type,
          scripSymbol: item.scrip_symbol,
          status: item.status ?? '',
        };
      });

      setCardsData(formatted);
      setPagination(paginationInfo);
    } catch (error) {
      console.error('Error fetching expert views:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    fetchExpertViews(page);
  }, [fetchExpertViews]);

  const handleExport = useCallback(async () => {
    try {
      setExporting(true);

      // Fetch all pages
      const firstResponse = await serverAddr.post('/publisher/expert_views/', {
        page_no: 1,
        page_per_entries: 1000,
      });
      const rawData = firstResponse?.data;
      const allData = Array.isArray(rawData) ? rawData : (rawData?.data || []);

      const rows = allData.map((item) => ({
        'Expert Name': item.expert_name ?? '',
        'Coin Name': item.scrip_title ?? '',
        'Direction': item.direction ?? '',
        'Mark Price': item.mark_price ?? '',
        'Lower Entry': item.lower_entry ?? '',
        'Upper Entry': item.upper_entry ?? '',
        'Entry Basis': item.entry_basis ?? '',
        'Target 1': item.target_1 ?? '',
        'Target 2': item.target_2 ?? '',
        'Stoploss': item.stop_loss_1 ?? '',
        'Leverage': item.leverage ?? '',
        'Signal Date': formatTimestamp(item.published_on),
        'Validity': item.target_period ?? '',
        'Logic': item.note ?? '',
        'Tags': Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags ?? ''),
        'Chart URL': item.chart ?? '',
        'Status': item.status ?? '',
        'Status Timestamp': formatTimestamp(item.status_timestamp),
        'Performance': item.performance ?? '',
        'Remaining Target': item.remaining_target ?? '',
        'Risk Reward Ratio 1': item.risk_reward_ratio_1 ?? '',
        'Risk Reward Ratio 2': item.risk_reward_ratio_2 ?? '',
        'Max Profit': item.max_profit ?? '',
        'Max Loss': item.max_loss ?? '',
        'Pushed to which exchanges': item.pushed_exchanges ?? '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Expert Views');
      XLSX.writeFile(workbook, 'expert_views.xlsx');
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  }, []);

  const filteredCards = searchFilter
    ? cardsData.filter((c) => c.coin.toLowerCase().includes(searchFilter.toLowerCase()))
    : cardsData;

  const totalPages = pagination?.total_pages || 1;

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="crypto-layout">
      <main className="content">
        <TopBar onFilterClick={() => setShowFilters(true)} onPublishClick={() => setPublishOpen(true)} activeFiltersCount={activeFiltersCount} onExportClick={handleExport} exporting={exporting} onSearchSelect={(coin) => setSearchFilter(coin.name)} onSearchClear={() => setSearchFilter('')} />

        {loading && (
          <div className="myview-loading">
            <div className="myview-spinner" />
          </div>
        )}

        {!loading && filteredCards.map((card) => (
          <ExpertCard key={card.id} data={card} />
        ))}

        {!loading && pagination && (
          <div className="pagination">
            <button
              className="page-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.previous_page_number}
            >
              &#8592;
            </button>

            {pageNumbers.map((page) => (
              <button
                key={page}
                className={`page-btn ${page === currentPage ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="page-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.next_page_number}
            >
              &#8594;
            </button>

            <span className="page-info">
              {pagination.current_page} / {pagination.total_pages} &nbsp;({pagination.total_entries} total)
            </span>
          </div>
        )}
      </main>

      <PublishEditModal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        onOpenLogic={() => {
          setPublishOpen(false);
        }}
      />

      {showFilters && (
        <div className="filter-popup">
          <div className="filter-popup-body">
            <FiltersSidebar isPopup selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} onClose={() => setShowFilters(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
